import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NotificationService } from '@progress/kendo-angular-notification';
import { DialogService } from '@progress/kendo-angular-dialog';
import { NotificationAdminService, DistributionList, DistributionListMember } from '../../../shared/services/notification-admin.service';

@Component({
    selector: 'app-distribution-lists',
    templateUrl: './distribution-lists.component.html',
    styleUrls: ['./distribution-lists.component.css'],
    standalone: false
})
export class DistributionListsComponent implements OnInit {
  lists: DistributionList[] = [];
  eventTypes: string[] = [];
  loading = false;
  selectedList: DistributionList | null = null;
  isEditing = false;
  isNew = false;

  listForm: FormGroup;
  memberForm: FormGroup;
  showAddMemberDialog = false;

  constructor(
    private notificationAdminService: NotificationAdminService,
    private notificationService: NotificationService,
    private dialogService: DialogService,
    private fb: FormBuilder
  ) {
    this.listForm = this.fb.group({
      id: [null],
      name: ['', [Validators.required, Validators.maxLength(100)]],
      description: ['', Validators.maxLength(500)],
      eventType: ['', Validators.required],
      isActive: [true]
    });

    this.memberForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      displayName: [''],
      isActive: [true]
    });
  }

  ngOnInit(): void {
    this.loadLists();
    this.loadEventTypes();
  }

  loadLists(): void {
    this.loading = true;
    this.notificationAdminService.getDistributionLists().subscribe({
      next: (lists) => {
        this.lists = lists;
        this.loading = false;
      },
      error: (err) => {
        this.showNotification('Error loading distribution lists', 'error');
        this.loading = false;
      }
    });
  }

  loadEventTypes(): void {
    this.notificationAdminService.getEventTypes().subscribe({
      next: (types) => {
        this.eventTypes = types;
      },
      error: (err) => {
        console.error('Error loading event types', err);
      }
    });
  }

  onSelectionChange(event: any): void {
    if (event.selectedRows && event.selectedRows.length > 0 && event.selectedRows[0]) {
      this.selectList(event.selectedRows[0].dataItem);
    }
  }

  selectList(list: DistributionList | undefined): void {
    if (!list) return;
    this.selectedList = list;
    this.isEditing = false;
    this.isNew = false;
  }

  createNew(): void {
    this.selectedList = null;
    this.isNew = true;
    this.isEditing = true;
    this.listForm.reset({ isActive: true });
  }

  editList(): void {
    if (this.selectedList) {
      this.isEditing = true;
      this.isNew = false;
      this.listForm.patchValue(this.selectedList);
    }
  }

  cancelEdit(): void {
    this.isEditing = false;
    this.isNew = false;
    this.listForm.reset();
  }

  saveList(): void {
    if (this.listForm.invalid) {
      return;
    }

    const formValue = this.listForm.value;
    this.loading = true;

    if (this.isNew) {
      // Exclude id field when creating a new distribution list
      const { id, ...listData } = formValue;
      const list: DistributionList = listData;
      
      this.notificationAdminService.createDistributionList(list).subscribe({
        next: (created) => {
          created.members = [];
          this.lists.push(created);
          this.selectedList = created;
          this.isEditing = false;
          this.isNew = false;
          this.loading = false;
          this.showNotification('Distribution list created successfully', 'success');
        },
        error: (err) => {
          this.showNotification('Error creating distribution list', 'error');
          this.loading = false;
        }
      });
    } else {
      const list: DistributionList = formValue;
      this.notificationAdminService.updateDistributionList(list.id!, list).subscribe({
        next: (updated) => {
          const index = this.lists.findIndex(l => l.id === updated.id);
          if (index >= 0) {
            updated.members = this.lists[index].members;
            this.lists[index] = updated;
          }
          this.selectedList = updated;
          this.isEditing = false;
          this.loading = false;
          this.showNotification('Distribution list updated successfully', 'success');
        },
        error: (err) => {
          this.showNotification('Error updating distribution list', 'error');
          this.loading = false;
        }
      });
    }
  }

  deleteList(): void {
    if (!this.selectedList) return;

    const dialogRef = this.dialogService.open({
      title: 'Confirm Delete',
      content: `Are you sure you want to delete the distribution list "${this.selectedList.name}"? This will also remove all members.`,
      actions: [
        { text: 'Cancel' },
        { text: 'Delete', themeColor: 'error' }
      ],
      width: 400
    });

    dialogRef.result.subscribe((result: any) => {
      if (result.text === 'Delete' && this.selectedList) {
        this.notificationAdminService.deleteDistributionList(this.selectedList.id!).subscribe({
          next: () => {
            this.lists = this.lists.filter(l => l.id !== this.selectedList!.id);
            this.selectedList = null;
            this.showNotification('Distribution list deleted successfully', 'success');
          },
          error: (err) => {
            this.showNotification('Error deleting distribution list', 'error');
          }
        });
      }
    });
  }

  openAddMemberDialog(): void {
    this.memberForm.reset({ isActive: true });
    this.showAddMemberDialog = true;
  }

  closeAddMemberDialog(): void {
    this.showAddMemberDialog = false;
    this.memberForm.reset();
  }

  addMember(): void {
    if (!this.selectedList || this.memberForm.invalid) {
      return;
    }

    const member: DistributionListMember = {
      ...this.memberForm.value,
      distributionListId: this.selectedList.id
    };

    this.notificationAdminService.addDistributionListMember(this.selectedList.id!, member).subscribe({
      next: (added) => {
        if (!this.selectedList!.members) {
          this.selectedList!.members = [];
        }
        this.selectedList!.members.push(added);
        this.closeAddMemberDialog();
        this.showNotification('Member added successfully', 'success');
      },
      error: (err) => {
        this.showNotification('Error adding member', 'error');
      }
    });
  }

  removeMember(member: DistributionListMember): void {
    if (!this.selectedList) return;

    const dialogRef = this.dialogService.open({
      title: 'Confirm Remove',
      content: `Are you sure you want to remove "${member.displayName || member.email}" from this list?`,
      actions: [
        { text: 'Cancel' },
        { text: 'Remove', themeColor: 'error' }
      ],
      width: 400
    });

    dialogRef.result.subscribe((result: any) => {
      if (result.text === 'Remove' && this.selectedList) {
        this.notificationAdminService.removeDistributionListMember(this.selectedList.id!, member.id!).subscribe({
          next: () => {
            this.selectedList!.members = this.selectedList!.members!.filter(m => m.id !== member.id);
            this.showNotification('Member removed successfully', 'success');
          },
          error: (err) => {
            this.showNotification('Error removing member', 'error');
          }
        });
      }
    });
  }

  getMemberCount(list: DistributionList): number {
    return list.members?.filter(m => m.isActive)?.length || 0;
  }

  private showNotification(message: string, type: 'success' | 'error' | 'warning' | 'info'): void {
    this.notificationService.show({
      content: message,
      type: { style: type, icon: true },
      position: { horizontal: 'right', vertical: 'top' },
      animation: { type: 'fade', duration: 400 },
      hideAfter: 3000
    });
  }
}
